import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useFontes } from '../../hooks/useFontes';
import { useMontadoras } from '../../hooks/useMontadoras';
import { Fonte } from '../../types/fonte';
import { TableList } from '../../components/TableList';
import { useFontCache } from '../../hooks/useFontCache';
import { Button } from '../../components/Button';
import { useInView } from 'react-intersection-observer';
import { MontadoraSelect } from '../../components/MontadoraSelect';
import { MedidorImpressao } from '../../components/MedidorImpressao';
import { TourImpressao } from '../../components/TourImpressao';
import { AvisoSaldoBaixo } from '../../components/AvisoSaldoBaixo';
import { ArrowRight, X, Star, History, RotateCcw } from 'lucide-react';
import { useSaldo, LIMITE_AVISO_SALDO } from '../../contexts/SaldoContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFontesFavoritas } from '../../hooks/useFontesFavoritas';
import { supabase } from '../../lib/supabase';
import { normalizarAjusteAlturaTermica } from '../../lib/impressaoTermica';
import { toast } from 'react-toastify';

// Interface para os dados da última impressão
interface UltimaImpressao {
  chassi: string;
  fonte: string;
  fonteId: string;
  nomeFonte: string;
  nomeMontadora: string;
  tipoImpressao: 'matricial' | 'termica';
  incluirBrasil: boolean;
  dataHora: string;
}

// Interface para o histórico de impressões
interface HistoricoImpressoes {
  impressoes: UltimaImpressao[];
  ultimoIndice: number;
}

// ===== Preferência de "Edição de Chassi" (lembrada no navegador) + segurança de tamanho de tela =====
// Guarda no cache do navegador (localStorage) se o usuário deixou a edição ligada ou desligada
// e reabre o modal do mesmo jeito na próxima vez. Segurança: se a janela for pequena demais para
// o modal grande (com edição), ele abre recolhido para o usuário conseguir enxergar tudo.
const CHAVE_EDICAO_HABILITADA = 'imprimirChassi.edicaoHabilitada';
const LARGURA_MIN_EDICAO = 760; // largura mínima de janela (px) para abrir com edição (2 colunas)
const ALTURA_MIN_EDICAO = 660;  // altura mínima de janela (px) para o modal grande caber sem cortar

const lerPreferenciaEdicao = (): boolean => {
  try {
    const salvo = localStorage.getItem(CHAVE_EDICAO_HABILITADA);
    return salvo === null ? true : salvo === '1'; // ligado por padrão na 1ª vez
  } catch {
    return true; // se o navegador bloquear o armazenamento, assume ligado
  }
};

const salvarPreferenciaEdicao = (habilitada: boolean): void => {
  try {
    localStorage.setItem(CHAVE_EDICAO_HABILITADA, habilitada ? '1' : '0');
  } catch {
    /* navegador pode bloquear o armazenamento (ex.: modo privado); ignora */
  }
};

const telaComportaEdicao = (): boolean =>
  window.innerWidth >= LARGURA_MIN_EDICAO && window.innerHeight >= ALTURA_MIN_EDICAO;

// ===== Auto-redução do modal para caber inteiro na tela (limite definido com o usuário) =====
// Se o modal não couber na janela, ele é reduzido (transform: scale) até caber SEM barras de rolagem.
// Há um limite: nunca reduz abaixo de ESCALA_MINIMA_MODAL (senão fica ilegível); abaixo disso,
// as barras de rolagem (vertical/horizontal) assumem. Limite medido pelo usuário: modal ~704x365 a
// partir do natural ~1100x570  ->  704/1100 = 365/570 = 0,64.
const ESCALA_MINIMA_MODAL = 0.64; // 64% — menor tamanho ainda legível/usável (ajustável aqui)
const MARGEM_LARGURA_MODAL = 0.95; // usa 95% da largura da janela (deixa um respiro nas laterais)
const MARGEM_ALTURA_MODAL = 0.9;   // usa 90% da altura da janela (deixa um respiro em cima/embaixo)

// ===== Tour guiado (tutorial da 1ª vez) =====
// Mostra um passo a passo apresentando a área de medição/edição quando o usuário abre o
// modal pela primeira vez NAQUELE navegador. Depois de visto, fica salvo no cache e não
// reaparece. Para reexibir (teste/demonstração), basta limpar esta chave do navegador.
const CHAVE_TUTORIAL_VISTO = 'imprimirChassi.tutorialVisto';                // tour da edição (2 passos)
const CHAVE_TUTORIAL_SIMPLES_VISTO = 'imprimirChassi.tutorialSimplesVisto'; // guia do modo simples (1 passo)

const lerTutorialVisto = (chave: string): boolean => {
  try {
    return localStorage.getItem(chave) === '1';
  } catch {
    return true; // se o navegador bloquear o cache, não insiste no tour
  }
};

const salvarTutorialVisto = (chave: string): void => {
  try {
    localStorage.setItem(chave, '1');
  } catch {
    /* navegador pode bloquear o armazenamento (ex.: modo privado); ignora */
  }
};

// Passos do tour. Os seletores apontam para os elementos marcados com data-tour no modal.
// (Edite os textos aqui para ajustar a explicação mostrada ao usuário.)
const PASSOS_TOUR = [
  {
    seletor: '[data-tour="coluna-modificar"]',
    titulo: 'Novidade: medir e modificar o chassi',
    texto:
      'Nesta área você pode ajustar o espaçamento e o tamanho do chassi e ver a medida estimada, em centímetros, antes de imprimir. Use apenas quando precisar adaptar o desenho.',
  },
  {
    seletor: '[data-tour="botao-edicao"]',
    titulo: 'Não quer medir nem editar?',
    texto:
      'Sem problema. Clique neste botão para desabilitar a medição e a edição e voltar ao modo simples de impressão. Sua escolha fica salva para as próximas vezes.',
  },
];

// Guia do MODO SIMPLES (edição desligada): 1 passo só, ensinando o botão "Medir e Editar Chassi".
const PASSOS_TOUR_SIMPLES = [
  {
    seletor: '[data-tour="botao-edicao"]',
    titulo: 'Precisa medir ou ajustar o chassi?',
    texto:
      'Clique em "Medir e Editar Chassi" para abrir as opções de tamanho e espaçamento e ver a medida estimada, em centímetros, antes de imprimir.',
  },
];

// ===== Aviso de saldo baixo =====
// Ao entrar na tela, aparece no máximo uma vez por sessão do navegador; depois de cada
// impressão, aparece sempre que o saldo estiver baixo, até o usuário marcar "Não mostrar mais".
const CHAVE_AVISO_SALDO_SESSAO = 'imprimirChassi.avisoSaldoBaixoMostrado';

const avisoSaldoJaMostradoNaSessao = (idUsuario: string): boolean => {
  try {
    return sessionStorage.getItem(CHAVE_AVISO_SALDO_SESSAO) === idUsuario;
  } catch {
    return false;
  }
};

const marcarAvisoSaldoMostradoNaSessao = (idUsuario: string): void => {
  try {
    sessionStorage.setItem(CHAVE_AVISO_SALDO_SESSAO, idUsuario);
  } catch {
    /* navegador pode bloquear o armazenamento (ex.: modo privado); ignora */
  }
};

const ImprimirChassi: React.FC = () => {
  // Estados para busca, texto de demonstração, fontes e controle do modal
  const [busca, setBusca] = useState('');
  const [textoDemo, setTextoDemo] = useState('');
  const { fontes, listLoading } = useFontes();
  const { montadoras = [], getLogoUrl } = useMontadoras();
  const { loadFontFromCache } = useFontCache();
  const [fontesCarregadas, setFontesCarregadas] = useState<Set<string>>(new Set());
  const [displayedItems, setDisplayedItems] = useState<number>(20); // Quantidade inicial de itens
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para controlar a abertura do modal

  // ===== Auto-redução do modal: calcula a escala para ele caber inteiro na tela (até o limite) =====
  const modalPanelRef = useRef<HTMLDivElement>(null);
  const modalOverlayRef = useRef<HTMLDivElement>(null); // fundo do modal (fixed inset-0): tem sempre o tamanho da janela
  const [escalaModal, setEscalaModal] = useState(1); // fator do transform: scale (1 = 100%, sem reduzir)
  // Tamanho do modal JÁ reduzido (px). O "embrulho" (wrapper) ocupa exatamente esse espaço, para o modal
  // ficar centralizado e, abaixo do limite, as barras de rolagem medirem o tamanho certo (e não o original).
  const [footprintModal, setFootprintModal] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = modalPanelRef.current;
    if (!isModalOpen || !el) return;
    const ajustar = () => {
      const alvo = modalPanelRef.current;
      if (!alvo) return;
      // offsetWidth/Height ignoram o transform => sempre o tamanho NATURAL (largura fixa pela classe w-[...];
      // altura = conteúdo real, já que tiramos o max-h do painel).
      const natW = alvo.offsetWidth;
      const natH = alvo.offsetHeight;
      const dispW = window.innerWidth * MARGEM_LARGURA_MODAL;
      const dispH = window.innerHeight * MARGEM_ALTURA_MODAL;
      const precisa = Math.min(1, dispW / natW, dispH / natH); // o quanto precisa encolher para caber
      const escala = Math.max(ESCALA_MINIMA_MODAL, precisa);   // nunca abaixo do limite legível
      setEscalaModal(escala);
      setFootprintModal({ w: natW * escala, h: natH * escala });
    };
    ajustar();
    const ro = new ResizeObserver(ajustar);
    ro.observe(el);                                  // pega alternar edição / mudança de conteúdo (muda natW/natH)
    if (modalOverlayRef.current) ro.observe(modalOverlayRef.current); // fundo = tamanho da janela: pega resize/zoom com segurança
    window.addEventListener('resize', ajustar);      // reforço (resize/zoom do navegador)
    return () => { ro.disconnect(); window.removeEventListener('resize', ajustar); };
  }, [isModalOpen]);
  // ===== fim auto-redução =====
  const [selectedFonte, setSelectedFonte] = useState<Fonte | null>(null); // Fonte selecionada
  const [modalChassi, setModalChassi] = useState(''); // Estado para o input do modal
  const [incluirBrasil, setIncluirBrasil] = useState(false); // Estado para o checkbox
  const [isSaldoModalOpen, setIsSaldoModalOpen] = useState(false); // Estado para o modal de saldo insuficiente
  const [mostrarControlesEdicao, setMostrarControlesEdicao] = useState(lerPreferenciaEdicao); // Visibilidade dos controles de edição — lembra a última escolha do usuário (cache do navegador)
  const [tourAtivo, setTourAtivo] = useState(false); // Tour guiado da 1ª vez (apresenta a área de medição/edição)
  const [tourSimplesAtivo, setTourSimplesAtivo] = useState(false); // Guia da 1ª vez no modo simples (ensina o botão Medir e Editar)
  const { saldo, loading: saldoLoading, avisoSilenciado, atualizarSaldo, buscarSaldoAtual, silenciarAviso } = useSaldo(); // Hook para acessar o saldo do usuário
  const { user } = useAuth(); // Hook para acessar o usuário logado
  const [isAvisoSaldoOpen, setIsAvisoSaldoOpen] = useState(false); // Aviso de créditos baixos
  const [verificarAvisoAposImpressao, setVerificarAvisoAposImpressao] = useState(false); // Pede a checagem do aviso com o saldo já atualizado
  const { toggleFavorito, isFonteFavorita } = useFontesFavoritas(); // Hook para gerenciar favoritos
  const [mostrarFavoritos, setMostrarFavoritos] = useState(false); // Estado para controlar a exibição de favoritos
  const [mostrarUltimaImpressao, setMostrarUltimaImpressao] = useState(false); // Estado para controlar a exibição da última impressão
  const [espacamento, setEspacamento] = useState(2); // Estado para controlar o espaçamento
  const [tamanho, setTamanho] = useState(24); // Estado para controlar o tamanho
  
  // Valor de custo por impressão (poderia vir de uma configuração do sistema)
  const CUSTO_IMPRESSAO = 1; // 1 unidade de saldo por impressão

  // Configuração do observer para detectar quando o usuário chega próximo ao final
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  // Função para obter o histórico de impressões do localStorage
  const obterHistoricoImpressoes = (): HistoricoImpressoes => {
    const dados = localStorage.getItem('historicoImpressoes');
    if (dados) {
      return JSON.parse(dados);
    }
    return {
      impressoes: [],
      ultimoIndice: -1
    };
  };

  // Função para salvar uma nova impressão no histórico
  const salvarUltimaImpressao = (
    chassi: string,
    fonte: Fonte,
    tipoImpressao: 'matricial' | 'termica'
  ) => {
    const novaImpressao: UltimaImpressao = {
      chassi: chassi,
      fonte: fonte.arquivo || '',
      fonteId: fonte.id.toString(),
      nomeFonte: fonte.nome,
      nomeMontadora: fonte.nome_montadora,
      tipoImpressao: tipoImpressao,
      incluirBrasil: incluirBrasil,
      dataHora: new Date().toISOString()
    };

    const historico = obterHistoricoImpressoes();
    
    // Adiciona a nova impressão no início do array
    historico.impressoes.unshift(novaImpressao);
    
    // Mantém apenas as últimas 50 impressões
    if (historico.impressoes.length > 50) {
      historico.impressoes = historico.impressoes.slice(0, 50);
    }
    
    // Atualiza o índice
    historico.ultimoIndice = (historico.ultimoIndice + 1) % 50;
    
    // Salva o histórico atualizado
    localStorage.setItem('historicoImpressoes', JSON.stringify(historico));
  };

  // Funções de conversão entre pixels e porcentagem
  const pixelsParaPorcentagem = (pixels: number, tamanhoOriginal: number): number => {
    if (tamanhoOriginal === 0) return 100;
    return Math.round((pixels / tamanhoOriginal) * 100);
  };

  const porcentagemParaPixels = (porcentagem: number, tamanhoOriginal: number): number => {
    return parseFloat(((porcentagem / 100) * tamanhoOriginal).toFixed(2));
  };

  // Calcula a porcentagem atual baseada no tamanho original da fonte selecionada
  const obterPorcentagemAtual = (): number => {
    const tamanhoOriginal = selectedFonte?.tamanho || 24;
    return pixelsParaPorcentagem(tamanho, tamanhoOriginal);
  };

  // Formata a porcentagem para exibição (sempre inteiro)
  const formatarPorcentagem = (valor: number): string => {
    return Math.round(valor).toString();
  };

  // Formata pixels para exibição (remove decimais desnecessários)
  const formatarPixels = (valor: number): string => {
    return valor % 1 === 0 ? valor.toString() : valor.toFixed(2);
  };

  // Atualiza o tamanho em pixels baseado na porcentagem
  const atualizarTamanhoPorPorcentagem = (porcentagem: number) => {
    const tamanhoOriginal = selectedFonte?.tamanho || 24;
    const novoTamanhoPixels = porcentagemParaPixels(porcentagem, tamanhoOriginal);
    setTamanho(novoTamanhoPixels);
  };

  // Calcula os limites de porcentagem baseados nos limites de pixels (8-72)
  const obterLimitesPorcentagem = () => {
    const tamanhoOriginal = selectedFonte?.tamanho || 24;
    const minPorcentagem = pixelsParaPorcentagem(8, tamanhoOriginal);
    const maxPorcentagem = pixelsParaPorcentagem(72, tamanhoOriginal);
    return { min: minPorcentagem, max: maxPorcentagem };
  };

  // Filtra as fontes com base na busca e na opção de mostrar favoritos
  const fontesFiltradas = (fontes || []).filter(fonte => {
    // Se estiver mostrando última impressão
    if (mostrarUltimaImpressao) {
      const historico = obterHistoricoImpressoes();
      // Verifica se a fonte está presente em alguma impressão do histórico usando o ID
      return historico.impressoes.some(impressao => impressao.fonteId === fonte.id.toString());
    }

    // Primeiro filtra por busca
    const matchesBusca = busca === '' || 
                        fonte.nome.toLowerCase().includes(busca.toLowerCase()) ||
                        fonte.nome_montadora.toLowerCase().includes(busca.toLowerCase());
    
    // Se estiver mostrando favoritos, filtra apenas as favoritas
    if (mostrarFavoritos) {
      return matchesBusca && isFonteFavorita(fonte.id);
    }
    
    return matchesBusca;
  });

  // Função para criar uma lista expandida de fontes com suas impressões
  const expandirFontesComImpressoes = (fontesParaExpandir: Fonte[]) => {
    if (!mostrarUltimaImpressao) return fontesParaExpandir;

    const historico = obterHistoricoImpressoes();
    const fontesExpandidas: (Fonte & { impressaoId?: string, impressaoChassi?: string })[] = [];

    // Para cada impressão no histórico, cria uma cópia da fonte correspondente
    historico.impressoes.forEach((impressao) => {
      const fonte = fontesParaExpandir.find(f => f.id.toString() === impressao.fonteId);
      if (fonte) {
        fontesExpandidas.push({
          ...fonte,
          impressaoId: impressao.dataHora, // Usa a data/hora como ID único da impressão
          impressaoChassi: impressao.chassi
        });
      }
    });

    return fontesExpandidas;
  };

  // Aplica a expansão nas fontes filtradas
  const fontesOrdenadas = expandirFontesComImpressoes(fontesFiltradas);

  // Carrega mais itens quando chega ao final
  useEffect(() => {
    if (inView && displayedItems < fontesOrdenadas.length) {
      setDisplayedItems(prev => Math.min(prev + 20, fontesOrdenadas.length));
    }
  }, [inView, fontesOrdenadas.length]);

  // Reseta a quantidade de itens exibidos quando houver uma nova busca ou alternar entre favoritos
  useEffect(() => {
    setDisplayedItems(20);
  }, [busca, mostrarFavoritos]);

  // Função para carregar fonte específica
  const carregarFonte = async (arquivo: string) => {
    if (!fontesCarregadas.has(arquivo)) {
      try {
        const success = await loadFontFromCache(arquivo);
        if (success) {
          setFontesCarregadas(prev => new Set([...prev, arquivo]));
        }
      } catch (error) {
        console.error('Erro ao carregar fonte:', error);
      }
    }
  };

  // Carrega fontes quando a lista for atualizada
  useEffect(() => {
    if (fontes) {
      fontes.forEach(fonte => {
        if (fonte.arquivo) {
          carregarFonte(fonte.arquivo);
        }
      });
    }
  }, [fontes]);

  // Função para abrir o modal e definir a fonte selecionada
  const handlePrintClick = (fonte: Fonte & { impressaoId?: string, impressaoChassi?: string }) => {
    setSelectedFonte(fonte);

    // Aplica a preferência salva (cache do navegador). Segurança: se a janela for pequena
    // demais para o modal com edição, abre recolhido para caber na tela (não altera a
    // preferência salva — só muda o que aparece nesta abertura).
    setMostrarControlesEdicao(lerPreferenciaEdicao() && telaComportaEdicao());
    
    // Sincroniza os valores padrão da fonte com os estados
    setEspacamento(fonte.espacamento || 2);
    setTamanho(fonte.tamanho || 24);
    
    if (mostrarUltimaImpressao) {
      // Se a fonte tem impressaoId e impressaoChassi, usa diretamente esses valores
      if (fonte.impressaoId && fonte.impressaoChassi) {
        setModalChassi(fonte.impressaoChassi);
        const historico = obterHistoricoImpressoes();
        const impressao = historico.impressoes.find(imp => imp.dataHora === fonte.impressaoId);
        setIncluirBrasil(impressao?.incluirBrasil || false);
      }
    } else {
      setModalChassi(textoDemo || '');
      setIncluirBrasil(false);
    }
    
    setIsModalOpen(true);
  };

  // Encerra o tour da edição e grava no cache que o usuário já o viu (não reaparece).
  const encerrarTour = () => {
    setTourAtivo(false);
    salvarTutorialVisto(CHAVE_TUTORIAL_VISTO);
  };

  // Encerra o guia do modo simples e grava no cache que já foi visto.
  const encerrarTourSimples = () => {
    setTourSimplesAtivo(false);
    salvarTutorialVisto(CHAVE_TUTORIAL_SIMPLES_VISTO);
  };

  // Dispara o guia certo ao abrir o modal — cada um só na 1ª vez (cache do navegador):
  // - com a edição habilitada: tour de 2 passos (apresenta a área de medição/edição);
  // - no modo simples (edição desligada): 1 passo, ensinando o botão "Medir e Editar Chassi".
  // Também dispara se o usuário alternar o modo depois (aí cada novidade aparece na 1ª vez dela).
  useEffect(() => {
    if (!isModalOpen) return;
    if (mostrarControlesEdicao && !lerTutorialVisto(CHAVE_TUTORIAL_VISTO)) {
      setTourAtivo(true);
    } else if (!mostrarControlesEdicao && !lerTutorialVisto(CHAVE_TUTORIAL_SIMPLES_VISTO)) {
      setTourSimplesAtivo(true);
    }
  }, [isModalOpen, mostrarControlesEdicao]);

  // Função para fechar o modal
  const closeModal = () => {
    setIsModalOpen(false);
    setIncluirBrasil(false); // Desmarca o checkbox ao fechar o modal
  };

  // Efeito para adicionar o listener de teclado
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    // Adiciona o listener
    document.addEventListener('keydown', handleKeyDown);

    // Remove o listener ao desmontar o componente
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Debita 1 crédito no servidor (função `debitar_impressao`), que também grava a data
  // da última impressão. É o banco que decide se há saldo, numa única operação, então
  // cliques repetidos ou abas simultâneas não deixam o saldo negativo.
  const debitarSaldo = async () => {
    if (!user) {
      console.error('[debitarSaldo] Usuário não encontrado');
      return false;
    }

    try {
      const { data: novoSaldo, error } = await supabase.rpc('debitar_impressao');

      if (error) {
        if (error.message?.includes('SALDO_INSUFICIENTE')) {
          console.warn('[debitarSaldo] Saldo insuficiente no servidor');
          await buscarSaldoAtual();
          return false;
        }
        throw error;
      }

      // Atualiza o saldo exibido com o valor devolvido pelo servidor
      atualizarSaldo(novoSaldo as number);

      return true;
    } catch (error) {
      console.error('[debitarSaldo] Erro ao debitar saldo:', error);
      
      // Em caso de erro, buscar saldo atual para verificar se houve inconsistência
      try {
        await buscarSaldoAtual();
      } catch (verificationError) {
        console.error('[debitarSaldo] Erro na verificação de saldo após falha:', verificationError);
      }
      
      return false;
    }
  };

  // Ajuste de altura da impressão térmica configurado pelo admin no cadastro do cliente.
  // Lido do servidor a cada impressão, para valer sem o cliente precisar sair e entrar.
  // Devolve 0 quando o ajuste está desligado (a grande maioria) e null se a leitura falhar.
  const buscarAjusteAlturaTermica = async (): Promise<number | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('usuarios')
      .select('ajusta_altura_termica, altura_termica_ajuste')
      .eq('id', user.id)
      .single();

    if (error || !data) {
      console.error('[buscarAjusteAlturaTermica] Erro ao ler ajuste de altura:', error);
      return null;
    }

    return data.ajusta_altura_termica ? normalizarAjusteAlturaTermica(data.altura_termica_ajuste) : 0;
  };

  // Aviso de créditos baixos: só para usuário e revenda (o admin não recebe), com saldo
  // entre 1 e o limite. Com saldo 0 quem avisa é o modal de saldo insuficiente.
  const deveAvisarSaldoBaixo =
    !!user &&
    (user.tipo === 'usuario' || user.tipo === 'revenda') &&
    !avisoSilenciado &&
    saldo >= 1 &&
    saldo <= LIMITE_AVISO_SALDO;

  // Cliente de revenda pede os créditos à revenda; os demais, ao administrador
  const destinatarioAvisoSaldo =
    user?.tipo === 'usuario' && user.id_revenda ? 'à sua revenda' : 'ao seu administrador';

  // Ao entrar na tela: uma vez por sessão do navegador
  useEffect(() => {
    if (saldoLoading || !user || !deveAvisarSaldoBaixo) return;
    if (avisoSaldoJaMostradoNaSessao(user.id)) return;
    marcarAvisoSaldoMostradoNaSessao(user.id);
    setIsAvisoSaldoOpen(true);
  }, [saldoLoading, user, deveAvisarSaldoBaixo]);

  // Depois de cada impressão: roda quando saldo e opção já vieram atualizados do servidor
  useEffect(() => {
    if (!verificarAvisoAposImpressao) return;
    setVerificarAvisoAposImpressao(false);
    if (deveAvisarSaldoBaixo) {
      setIsAvisoSaldoOpen(true);
    }
  }, [verificarAvisoAposImpressao, deveAvisarSaldoBaixo]);

  const fecharAvisoSaldo = async (silenciar: boolean) => {
    setIsAvisoSaldoOpen(false);
    if (!silenciar) return;
    try {
      await silenciarAviso();
    } catch (error) {
      console.error('[fecharAvisoSaldo] Erro ao salvar a preferência do aviso:', error);
      toast.error('Não foi possível salvar sua preferência. O aviso pode aparecer novamente.');
    }
  };

  // Função para imprimir
  const handlePrint = async (tipoImpressao: 'matricial' | 'termica') => {
    if (selectedFonte && (modalChassi || textoDemo)) {
      // CORREÇÃO: Verificar se saldo ainda está carregando
      if (saldoLoading) {
        return;
      }

      // Na térmica, lê o ajuste de altura ANTES do débito: se a leitura falhar, cancela sem
      // gastar crédito, em vez de imprimir na altura padrão um cliente que precisa de ajuste.
      let ajusteAlturaTermica = 0;
      if (tipoImpressao === 'termica') {
        const ajuste = await buscarAjusteAlturaTermica();
        if (ajuste === null) {
          toast.error('Não foi possível carregar a configuração da impressora térmica. Nenhum crédito foi debitado, tente novamente.');
          return;
        }
        ajusteAlturaTermica = ajuste;
      }

      try {
        // CORREÇÃO CRÍTICA: Sempre buscar saldo real do servidor primeiro
        // Buscar saldo real do servidor antes de qualquer validação
        const saldoReal = await buscarSaldoAtual();

        // Verificar se o saldo real é suficiente
        if (saldoReal < CUSTO_IMPRESSAO) {
          setIsSaldoModalOpen(true);
          return;
        }

        // Debita o saldo do usuário
        const debitoRealizado = await debitarSaldo();

        if (!debitoRealizado) {
          setIsSaldoModalOpen(true);
          return;
        }
      } catch (error: any) {
        console.error('[handlePrint] Erro durante verificação/débito:', error);
        
        // Verificar se é erro de JWT expirado
        if (error?.code === 'PGRST301' || error?.message?.includes('JWT expired')) {
          console.warn('[handlePrint] JWT expirado detectado, sistema já deve estar redirecionando');
          // Não mostrar modal de saldo, pois o SaldoContext já está lidando com isso
          return;
        }
        
        // Para outros erros, mostrar modal de saldo insuficiente
        setIsSaldoModalOpen(true);
        return;
      }
      
      const params = new URLSearchParams({
        chassi: (modalChassi || textoDemo).toUpperCase(),
        fonte: selectedFonte.arquivo || '',
        tamanho: tamanho.toString(),
        espacamento: espacamento.toString(),
        incluir_brasil: incluirBrasil.toString()
      });

      // Só vai na URL quando o cliente tem ajuste; sem ele a térmica usa a altura padrão
      if (tipoImpressao === 'termica' && ajusteAlturaTermica !== 0) {
        params.set('ajuste_altura', ajusteAlturaTermica.toString());
      }

      // Salva os dados da impressão no localStorage
      salvarUltimaImpressao(
        (modalChassi || textoDemo).toUpperCase(),
        selectedFonte,
        tipoImpressao
      );
      
      // Modificando para usar window.open com configurações específicas
      const printWindow = window.open(
        `/chassi/${tipoImpressao}?${params.toString()}`,
        '_blank',
        'noopener,noreferrer'
      );
      
      // Garantir que a janela principal não fique bloqueada
      if (printWindow) {
        printWindow.opener = null;
      }

      closeModal();

      // Só depois de abrir a impressão, para o aviso não atrasar a nova aba
      setVerificarAvisoAposImpressao(true);
    }
  };

  // Função para alternar a visualização da última impressão
  const toggleUltimaImpressao = () => {
    const novoEstado = !mostrarUltimaImpressao;
    setMostrarUltimaImpressao(novoEstado);
    if (novoEstado) {
      setMostrarFavoritos(false);
      setTextoDemo('');
    }
  };

  // Modificar o array columns para usar o textoDemo quando disponível
  const columns: any = [
    { 
      header: 'Fonte', 
      accessor: 'nome' as const,
      width: '25%',
      render: (fonte: Fonte & { impressaoChassi?: string }) => (
        <span>
          <span className="text-blue-600 font-semibold">{fonte.nome_montadora}</span> - {fonte.nome}
        </span>
      )
    },
    { 
      header: 'Demonstração', 
      accessor: 'demonstracao' as const,
      width: mostrarUltimaImpressao ? '50%' : '75%',
      render: (fonte: Fonte & { impressaoChassi?: string }) => {
        if (mostrarUltimaImpressao && fonte.impressaoChassi) {
          return (
            <div>
              <span 
                className="break-all whitespace-normal overflow-hidden block"
                style={{ 
                  fontFamily: fonte.arquivo ? `"${fonte.arquivo.replace('.ttf', '')}"` : 'inherit',
                  fontSize: '24px',
                  letterSpacing: '2px',
                  lineHeight: '1.5',
                  wordBreak: 'break-all'
                }}
              >
                {fonte.impressaoChassi}
              </span>
            </div>
          );
        }

        return (
          <div className="w-full">
            <span 
              className="break-all whitespace-normal overflow-hidden block" 
              style={{ 
                fontFamily: fonte.arquivo ? `"${fonte.arquivo.replace('.ttf', '')}"` : 'inherit',
                fontSize: '24px',
                letterSpacing: '2px',
                lineHeight: '1.5',
                wordBreak: 'break-all',
                maxWidth: '100%',
                hyphens: 'auto'
              }}
            >
              {textoDemo || fonte.demonstracao || '-'}
            </span>
          </div>
        );
      }
    },
    // Adiciona a coluna de data/hora somente quando mostrarUltimaImpressao estiver ativo
    ...(mostrarUltimaImpressao ? [{
      header: 'Data/Hora',
      width: '25%',
      render: (fonte: Fonte & { impressaoId?: string }) => {
        // impressaoId é a data/hora ISO
        if (fonte.impressaoId) {
          const data = new Date(fonte.impressaoId);
          return (
            <span>
              {data.toLocaleDateString()} {data.toLocaleTimeString()}
            </span>
          );
        }
        return null;
      }
    }] : [])
  ];

  // Definindo as ações, incluindo a nova ação para o botão "Imprimir" e favoritos
  const actions = [
    {
      icon: (
        <Button className="flex items-center gap-2 text-white">
          Selecionar <ArrowRight size={16} />
        </Button>
      ),
      onClick: (item: Fonte & { impressaoId?: string, impressaoChassi?: string }) => handlePrintClick(item),
      className: "text-blue-600 hover:text-blue-900"
    },
    {
      render: (fonte: Fonte & { impressaoId?: string, impressaoChassi?: string }) => (
        <button 
          className="p-2 rounded-full hover:bg-gray-100"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorito(fonte);
          }}
        >
          {isFonteFavorita(fonte.id) ? (
            <Star size={20} fill="#eab308" className="text-yellow-500" />
          ) : (
            <Star size={20} className="text-gray-400" />
          )}
        </button>
      ),
      onClick: () => {/* Esta função é necessária pela interface, mas não será usada */},
      className: "text-gray-600 hover:text-gray-900"
    }
  ];

  if (listLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentItems = fontesOrdenadas.slice(0, displayedItems);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 sm:gap-0 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Imprimir Chassi</h1>   

        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto sm:items-center">
          <div className="relative flex-1 sm:w-[220px]">
            <input
              type="text"
              className={`w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors uppercase placeholder:text-sm ${
                mostrarUltimaImpressao ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Digite o chassi..."
              value={textoDemo}
              onChange={(e) => setTextoDemo(e.target.value.toUpperCase())}
              disabled={mostrarUltimaImpressao}
            />
            {textoDemo && !mostrarUltimaImpressao && (
              <button
                onClick={() => setTextoDemo('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            )}
          </div>

          <MontadoraSelect
            className="w-[100%] sm:w-[260px]" 
            montadoras={montadoras}
            value={busca}
            onChange={(value) => setBusca(value)}
            getLogoUrl={getLogoUrl}
            disabled={mostrarUltimaImpressao}
          />
          
          {/* Botão para alternar entre todas as fontes e favoritas */}
          <button
            onClick={() => setMostrarFavoritos(!mostrarFavoritos)}
            className={`p-2 rounded-md flex items-center justify-center gap-1 transition-colors ${
              mostrarFavoritos 
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                : 'bg-gray-100 text-gray-700 border border-gray-300'
            } ${mostrarUltimaImpressao ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
            disabled={mostrarUltimaImpressao}
          >
            <Star size={16} className={mostrarFavoritos ? 'fill-yellow-500 text-yellow-500' : ''} />
            Favoritas
          </button>

          {/* Botão da impressora */}
          <button
            onClick={toggleUltimaImpressao}
            className={`p-2 rounded-md flex items-center justify-center gap-1 transition-colors ${
              mostrarUltimaImpressao
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <History size={16} className={mostrarUltimaImpressao ? 'text-blue-500' : ''} />
            Reimprimir
          </button>
        </div>        
      </div>

      {/* Exibir mensagem quando não houver última impressão */}
      {mostrarUltimaImpressao && fontesOrdenadas.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 text-center">
          <p className="text-blue-800">
            Nenhuma impressão foi realizada ainda.
          </p>
        </div>
      )}

      {/* Exibir mensagem quando não houver favoritos */}
      {mostrarFavoritos && fontesOrdenadas.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4 text-center">
          <p className="text-yellow-800">
            Você ainda não tem fontes favoritas. Clique no ícone <Star size={16} className="inline" /> para adicionar.
          </p>
        </div>
      )}

      <div className="w-full overflow-hidden">
        <TableList
          data={currentItems}
          columns={columns}
          actions={actions}
        />
      </div>

      {/* Elemento observador para detectar o final da lista */}
      <div ref={ref} className="h-10">
        {displayedItems < fontesOrdenadas.length && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* Modal para impressão */}
      {isModalOpen && (
        <div ref={modalOverlayRef} className="fixed inset-0 flex bg-black bg-opacity-50 p-4 z-10 overflow-auto">
          {/* Embrulho com o tamanho JÁ reduzido: centraliza (m-auto) e, abaixo do limite, faz as barras de rolagem medirem o tamanho certo (e não o original) */}
          <div className="m-auto" style={footprintModal ? { width: footprintModal.w, height: footprintModal.h, flexShrink: 0 } : undefined}>
            <div
              ref={modalPanelRef}
              style={{ transform: escalaModal !== 1 ? `scale(${escalaModal})` : undefined, transformOrigin: 'top left' }}
              className={`relative bg-white p-4 sm:px-10 sm:py-6 rounded-lg shadow-lg ${mostrarControlesEdicao ? 'w-[1100px]' : 'w-[500px]'}`}
            >
            {/* Botão fechar */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
            {selectedFonte && (
              <div className={mostrarControlesEdicao ? 'flex gap-8 items-start' : ''}>
                {/* Coluna ESQUERDA: Modificar Chassi (visível só com a edição habilitada) */}
                {mostrarControlesEdicao && (
                  <div data-tour="coluna-modificar" className="flex-1 min-w-[440px] border-r border-gray-200 pr-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Modificar Chassi</h2>
                    <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                      O chassi selecionado sempre vem com seu tamanho original de desenho.
                    </p>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      Apenas use as opções de modificação para fazer adaptações de tamanho e espaçamento caso considere necessário.
                    </p>

                    {/* Controles de Espaçamento */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Espaçamento ({formatarPixels(espacamento)})
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min="0"
                          max="20"
                          step="0.5"
                          value={espacamento}
                          onChange={(e) => setEspacamento(parseFloat(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <button
                          onClick={() => setEspacamento(selectedFonte?.espacamento || 2)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Restaurar valor padrão"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Controles de Tamanho */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tamanho ({formatarPorcentagem(obterPorcentagemAtual())}%)
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={obterLimitesPorcentagem().min}
                          max={obterLimitesPorcentagem().max}
                          step="1"
                          value={obterPorcentagemAtual()}
                          onChange={(e) => atualizarTamanhoPorPorcentagem(parseInt(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <button
                          onClick={() => setTamanho(selectedFonte?.tamanho || 24)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Restaurar valor padrão (100%)"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Medição (novo): comprimento e altura estimados, antes de imprimir */}
                    <MedidorImpressao
                      fontFamily={selectedFonte.arquivo ? selectedFonte.arquivo.replace('.ttf', '') : ''}
                      texto={modalChassi}
                      tamanho={tamanho}
                      espacamento={espacamento}
                      incluirBrasil={incluirBrasil}
                    />
                  </div>
                )}

                {/* Coluna DIREITA: Imprimir Chassi (sempre visível) */}
                <div className={mostrarControlesEdicao ? 'w-[410px] shrink-0' : 'w-full'}>
                  <h2 className="text-2xl font-bold text-gray-800 mb-8">Imprimir Chassi</h2>
                  <label className="block mb-2">
                    Fonte: <span className="text-blue-600">{selectedFonte.nome_montadora}</span> - {selectedFonte.nome}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="DIGITE O CHASSI..."
                      value={modalChassi}
                      onChange={(e) => setModalChassi(e.target.value.toUpperCase())}
                      style={{
                        fontFamily: selectedFonte.arquivo ? `"${selectedFonte.arquivo.replace('.ttf', '')}"` : 'inherit',
                        fontSize: `${tamanho}px`,
                        letterSpacing: `${espacamento}px`,
                        textTransform: 'uppercase'
                      }}
                      className="p-2 w-full border rounded-md border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors [&::placeholder]:font-sans [&::placeholder]:text-sm [&::placeholder]:normal-case [&::placeholder]:tracking-normal [&::placeholder]:text-gray-400"
                    />

                    {/* Check do Brasil e Botão de Medição/Edição */}
                    <div className='flex items-center justify-between mt-4 mb-6'>
                      <button
                        data-tour="botao-edicao"
                        onClick={() => {
                          const novo = !mostrarControlesEdicao;
                          setMostrarControlesEdicao(novo);
                          salvarPreferenciaEdicao(novo); // lembra a escolha no navegador (cache)
                        }}
                        className={`p-2 rounded-md flex items-center justify-center transition-colors ${
                          mostrarControlesEdicao
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {mostrarControlesEdicao ? 'Desabilitar Medição e Edição' : 'Medir e Editar Chassi'}
                      </button>

                      <div className='flex items-center'>
                        <input
                          type="checkbox"
                          name="incluir_brasil"
                          id="incluir_brasil"
                          checked={incluirBrasil}
                          onChange={(e) => setIncluirBrasil(e.target.checked)}
                        />
                        <label htmlFor="incluir_brasil" className='ml-2'>Incluir "BRASIL"</label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-10">
                    <Button
                      onClick={() => handlePrint('matricial')}
                      className="mr-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Imprimir Matricial
                    </Button>
                    <Button
                      onClick={() => handlePrint('termica')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Imprimir Térmica
                    </Button>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Tour guiado da 1ª vez: apresenta a área de medição/edição (desenhado por cima do modal). */}
      {isModalOpen && mostrarControlesEdicao && tourAtivo && (
        <TourImpressao passos={PASSOS_TOUR} aoFechar={encerrarTour} />
      )}

      {/* Guia da 1ª vez no modo simples: ensina o botão Medir e Editar Chassi. */}
      {isModalOpen && !mostrarControlesEdicao && tourSimplesAtivo && (
        <TourImpressao passos={PASSOS_TOUR_SIMPLES} aoFechar={encerrarTourSimples} />
      )}

      {/* Modal para saldo insuficiente */}
      {isSaldoModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <button
                onClick={() => setIsSaldoModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
              
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ops! Houve um erro na geração de sua impressão!
                </h3>
              </div>
              
              <div className="text-gray-700 space-y-3">               
                
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-2">Verifique seu saldo:</p>
                  <p className="text-blue-700 mb-2">Consulte o valor no canto superior direito da tela</p>                  
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Sem saldo: Solicite saldo ao administrador.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-600 mr-2">🔐</span>
                    <span>Sessão expirada? Faça logout e entre novamente no sistema.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2">⟳</span>
                    <span>Possui saldo? Saia e entre novamente no sistema para verificar possíveis atualizações, em seguida tente novamente.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-600 mr-2">💬</span>
                    <span>Problema persiste? Contate o suporte técnico.</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setIsSaldoModalOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aviso de créditos baixos */}
      {isAvisoSaldoOpen && (
        <AvisoSaldoBaixo
          saldo={saldo}
          destinatario={destinatarioAvisoSaldo}
          aoFechar={fecharAvisoSaldo}
        />
      )}
    </div>
  );
};

export default ImprimirChassi;